// Calendar service for managing events
import { createClient } from '@blinkdotnew/sdk';
import type { CalendarEvent, CalendarEventInput } from '../types/calendar';
import { getValidAccessToken } from './oauthService';
import { toast } from 'sonner';
import { requestManager } from '@/lib/requestManager';
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

export class CalendarService {
  // Sync calendar events from Gmail/Outlook using the comprehensive getCalendarEventsByWeek function
  async syncCalendarEvents(userId: string): Promise<number> {
    try {
      // Check if user is authenticated first
      if (!blink.auth.isAuthenticated()) {
        console.log('User not authenticated - skipping calendar sync');
        return 0;
      }

      // Get JWT token for authentication
      const token = localStorage.getItem('access-token');
      
      if (!token) {
        console.log('No access token available - skipping calendar sync');
        return 0;
      }

      // Use the comprehensive getCalendarEventsByWeek function which handles all providers
      const response = await fetch('https://ben3ou87--getCalendarEventsByWeek.functions.blink.new', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const totalSynced = data.totalEvents || 0;
        
        if (totalSynced > 0) {
          console.log(`✅ Synced ${totalSynced} calendar events`);
        } else {
          console.log('No calendar events to sync (no accounts connected or no events found)');
        }
        
        return totalSynced;
      } else {
        const error = await response.json();
        console.log('Calendar sync skipped:', error.message || 'No accounts connected');
        return 0;
      }
    } catch (error) {
      // Silent fail - calendar sync is optional background operation
      console.log('Calendar sync unavailable:', error instanceof Error ? error.message : 'Unknown error');
      return 0;
    }
  }
  // Create a new calendar event
  async createEvent(userId: string, eventData: CalendarEventInput): Promise<CalendarEvent> {
    // Check for potential duplicates before creating
    // Look for events with same title and start time within 1 minute window
    const startTime = eventData.startTime.toISOString();
    const existingEvents = await blink.db.calendarEventsEnhanced.list({
      where: { userId, title: eventData.title }
    });
    
    // Check if there's a duplicate event with same start time (within 1 minute)
    const isDuplicate = existingEvents.some(evt => {
      const evtStart = new Date(evt.startTime || evt.start_time);
      const newStart = eventData.startTime;
      const timeDiff = Math.abs(evtStart.getTime() - newStart.getTime());
      // Consider it a duplicate if within 1 minute
      return timeDiff < 60000;
    });
    
    if (isDuplicate) {
      console.warn('⚠️ Duplicate event detected (same title and time), skipping creation');
      // Return the existing event instead of creating a duplicate
      const existing = existingEvents.find(evt => {
        const evtStart = new Date(evt.startTime || evt.start_time);
        const timeDiff = Math.abs(evtStart.getTime() - eventData.startTime.getTime());
        return timeDiff < 60000;
      });
      if (existing) {
        return this.transformEvent(existing);
      }
    }
    
    // Generate unique ID with more entropy to prevent collisions
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 12);
    const id = `event_${timestamp}_${random}`;
    
    try {
      // Create in enhanced table with category and color support
      await blink.db.calendarEventsEnhanced.create({
        id,
        userId,
        accountId: 'manual',
        source: 'manual',
        providerEventId: id,
        calendarId: 'default',
        title: eventData.title,
        description: eventData.description || '',
        location: eventData.location || '',
        startTime: eventData.startTime.toISOString(),
        endTime: eventData.endTime.toISOString(),
        startDate: eventData.startTime.toISOString().split('T')[0],
        endDate: eventData.endTime.toISOString().split('T')[0],
        timezone: 'UTC',
        isAllDay: eventData.isAllDay ? '1' : '0',
        isRecurring: '0',
        eventType: eventData.eventType || 'event',
        attendees: JSON.stringify(eventData.attendees || []),
        attendeeCount: eventData.attendees?.length || 0,
        status: 'confirmed',
        visibility: 'public',
        reminders: JSON.stringify({ reminderMinutes: eventData.reminderMinutes || 0 }),
        category: (eventData as any).category || 'Uncategorized',
        color: (eventData as any).color || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      const createdEvent = await this.getEvent(id);
      if (!createdEvent) {
        throw new Error('Failed to create event');
      }
      console.log('✅ Event created successfully:', id);
      return createdEvent;
    } catch (error: any) {
      // Log the full error for debugging
      console.error('❌ Error creating event:', error);
      
      // Handle constraint violations - this means the event already exists
      if (error?.message?.includes('duplicate') || error?.message?.includes('Constraint violation')) {
        console.warn('⚠️ Constraint violation detected - event may already exist');
        
        // Try to find and return the existing event
        const existingByTitle = await blink.db.calendarEventsEnhanced.list({
          where: { userId, title: eventData.title }
        });
        
        const match = existingByTitle.find(evt => {
          const evtStart = new Date(evt.startTime || evt.start_time);
          const timeDiff = Math.abs(evtStart.getTime() - eventData.startTime.getTime());
          return timeDiff < 60000; // Within 1 minute
        });
        
        if (match) {
          console.log('✅ Returning existing event instead:', match.id);
          return this.transformEvent(match);
        }
        
        // If we can't find the duplicate, throw a user-friendly error
        toast.error('This event already exists in your calendar');
        throw new Error('Duplicate event - event with same details already exists');
      }
      
      throw error;
    }
  }

  // Get a single event by ID
  async getEvent(id: string): Promise<CalendarEvent | null> {
    const events = await requestManager.execute(
      `calendar_event:${id}`,
      () => blink.db.calendarEventsEnhanced.list({
        where: { id }
      }),
      { priority: 8, cacheTTL: 60000 }
    );

    if (events.length === 0) {
      return null;
    }

    return this.transformEvent(events[0]);
  }

  // Get all events for a user
  async getUserEvents(userId: string): Promise<CalendarEvent[]> {
    const events = await requestManager.execute(
      `calendar_events:${userId}`,
      () => blink.db.calendarEventsEnhanced.list({
        where: { userId },
        orderBy: { startTime: 'asc' }
      }),
      { priority: 8, cacheTTL: 60000 }
    );

    return events.map(e => this.transformEvent(e));
  }

  // Get events for a specific date range
  async getEventsInRange(userId: string, startDate: Date, endDate: Date): Promise<CalendarEvent[]> {
    const events = await requestManager.execute(
      `calendar_events:${userId}`,
      () => blink.db.calendarEventsEnhanced.list({
        where: { userId },
        orderBy: { startTime: 'asc' }
      }),
      { priority: 8, cacheTTL: 60000 }
    );

    // Filter by date range
    return events
      .map(e => this.transformEvent(e))
      .filter(event => {
        const eventStart = new Date(event.startTime);
        return eventStart >= startDate && eventStart <= endDate;
      });
  }

  // Update an existing event
  async updateEvent(id: string, updates: Partial<CalendarEventInput>): Promise<CalendarEvent | null> {
    // Check if event exists first
    const existingEvent = await this.getEvent(id);
    if (!existingEvent) {
      console.warn(`Event ${id} not found, skipping update`);
      return null;
    }

    const updateData: any = {
      updatedAt: new Date().toISOString()
    };

    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.startTime !== undefined) updateData.startTime = updates.startTime.toISOString();
    if (updates.endTime !== undefined) updateData.endTime = updates.endTime.toISOString();
    if (updates.eventType !== undefined) updateData.eventType = updates.eventType;
    if (updates.location !== undefined) updateData.location = updates.location;
    if (updates.attendees !== undefined) updateData.attendees = JSON.stringify(updates.attendees);
    if (updates.isAllDay !== undefined) updateData.isAllDay = updates.isAllDay ? '1' : '0';
    if (updates.reminderMinutes !== undefined) updateData.reminderMinutes = updates.reminderMinutes;
    if (updates.color !== undefined) updateData.color = updates.color;
    
    // AI-related fields
    if ((updates as any).category !== undefined) updateData.category = (updates as any).category;
    if ((updates as any).aiSummary !== undefined) updateData.aiSummary = (updates as any).aiSummary;
    if ((updates as any).emailLinks !== undefined) updateData.emailLinks = JSON.stringify((updates as any).emailLinks);

    try {
      await blink.db.calendarEventsEnhanced.update(id, updateData);
    } catch (error: any) {
      // Handle validation errors gracefully
      if (error.code === 'VALIDATION_ERROR' && error.message?.includes('Column does not exist')) {
        console.error('Database schema validation error:', error);
        // Remove unsupported fields and retry
        const { category, ...safeUpdateData } = updateData;
        await blink.db.calendarEventsEnhanced.update(id, safeUpdateData);
      } else {
        throw error;
      }
    }

    return this.getEvent(id);
  }

  // Delete an event
  async deleteEvent(id: string): Promise<void> {
    await blink.db.calendarEventsEnhanced.delete(id);
  }

  // Get events for a specific date
  async getEventsForDate(userId: string, date: Date): Promise<CalendarEvent[]> {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    return this.getEventsInRange(userId, startOfDay, endOfDay);
  }

  // Helper to transform database record to CalendarEvent
  private transformEvent(dbEvent: any): CalendarEvent {
    // Handle both snake_case and camelCase field names
    const userId = dbEvent.user_id || dbEvent.userId;
    const startTime = dbEvent.start_time || dbEvent.startTime;
    const endTime = dbEvent.end_time || dbEvent.endTime;
    const eventType = dbEvent.event_type || dbEvent.eventType;
    const isAllDay = Number(dbEvent.is_all_day !== undefined ? dbEvent.is_all_day : dbEvent.isAllDay) > 0;
    const reminderMinutes = dbEvent.reminder_minutes || dbEvent.reminderMinutes;
    const aiSummary = dbEvent.ai_summary || dbEvent.aiSummary;
    const emailLinks = dbEvent.email_links || dbEvent.emailLinks;
    const createdAt = dbEvent.created_at || dbEvent.createdAt;
    const updatedAt = dbEvent.updated_at || dbEvent.updatedAt;
    
    return {
      id: dbEvent.id,
      userId,
      title: dbEvent.title,
      description: dbEvent.description || undefined,
      startTime: new Date(startTime),
      endTime: new Date(endTime),
      eventType: eventType as any,
      location: dbEvent.location || undefined,
      attendees: dbEvent.attendees ? safeJsonParse(dbEvent.attendees) : undefined,
      isAllDay,
      reminderMinutes: reminderMinutes || undefined,
      color: dbEvent.color || undefined,
      category: dbEvent.category || undefined,
      aiSummary: aiSummary || undefined,
      emailLinks: emailLinks ? safeJsonParse(emailLinks) : undefined,
      createdAt: new Date(createdAt),
      updatedAt: new Date(updatedAt)
    };
  }
}

export const calendarService = new CalendarService();
